const RECIPE_BASE_SERVINGS = 1;

function deriveFallbackIngredientCost(recipe = {}) {
  const rows = Array.isArray(recipe?.scaledIngredients) && recipe.scaledIngredients.length > 0
    ? recipe.scaledIngredients
    : (Array.isArray(recipe?.malzemeler) && recipe.malzemeler.length > 0
      ? recipe.malzemeler
      : (Array.isArray(recipe?.ingredients) ? recipe.ingredients : []));

  if (!rows.length) return 0;

  return rows.reduce((sum, row) => {
    const gramsLike =
      Number(row?.scaledGramsTotal) ||
      Number(row?.gramsTotal) ||
      Number(row?.amountNormalized) ||
      Number(row?.amount) ||
      Number(row?.quantity) ||
      Number(row?.miktar) ||
      0;
    const pricePerGram =
      Number(row?.pricePerGram) ||
      Number(row?.ingredientPricePerGram) ||
      Number(row?.normalizedPricePerGram) ||
      0;

    if (!(gramsLike > 0) || !(pricePerGram > 0)) return sum;
    return sum + (gramsLike * pricePerGram);
  }, 0);
}

function resolveBaseServings(recipe) {
  return RECIPE_BASE_SERVINGS;
}

export function resolveRecipeScalingContext(recipe = {}, currentPortion = null) {
  const baseServings = resolveBaseServings(recipe);
  const targetPortion =
    Number(currentPortion) ||
    Number(recipe?.presentation?.appliedPortion) ||
    Number(recipe?.targetPortion) ||
    baseServings;

  const hasScaledIngredientsPayload = Array.isArray(recipe?.scaledIngredients) && recipe.scaledIngredients.length > 0;
  const isPreScaled = !!(
    recipe?.isPreScaled ||
    recipe?.presentation?.isPreScaled ||
    recipe?.presentation?.isAlreadyScaled ||
    recipe?.priceSnapshot?.isAlreadyScaled ||
    recipe?.priceSnapshot?.isScaled ||
    hasScaledIngredientsPayload
  );

  const appliedPortion =
    Number(recipe?.presentation?.appliedPortion) ||
    Number(recipe?.targetPortion) ||
    (isPreScaled ? targetPortion : baseServings);

  const effectiveMultiplier = isPreScaled
    ? (targetPortion / Math.max(1, appliedPortion))
    : (targetPortion / Math.max(1, baseServings));

  return {
    baseServings,
    targetPortion,
    appliedPortion,
    isPreScaled,
    effectiveMultiplier
  };
}

export function calculateRecipeCostDisplay(recipe, currentPortion = null) {
  if (recipe?.priceSnapshot) {
    const snapshotPortionBasis = Number(
      recipe?.priceSnapshot?.portionBasis ??
      recipe?.presentation?.appliedPortion ??
      recipe?.targetPortion ??
      0
    );
    const portionBasis = snapshotPortionBasis > 0 ? snapshotPortionBasis : resolveBaseServings(recipe);
    const scaling = resolveRecipeScalingContext(recipe, currentPortion);
    const targetPortion = scaling.targetPortion;
    const isSnapshotAlreadyScaled = scaling.isPreScaled;
    const appliedPortion = scaling.appliedPortion || portionBasis;
    const effectiveMultiplier = isSnapshotAlreadyScaled
      ? (targetPortion / Math.max(1, appliedPortion))
      : (targetPortion / Math.max(1, portionBasis));

    const rawSnapshotBreakdown = Array.isArray(recipe.priceSnapshot.breakdown)
      ? recipe.priceSnapshot.breakdown.map((item) => Number(item?.total || 0))
      : [];
    const snapshotBreakdown = rawSnapshotBreakdown.map((line) => (Number(line) || 0) * effectiveMultiplier);
    const snapshotBreakdownTotal = rawSnapshotBreakdown.reduce(
      (sum, line) => sum + (Number(line) || 0),
      0
    );
    const snapshotTotal = Number(recipe?.priceSnapshot?.total);
    const fallbackTotal = Number(
      recipe?.presentation?.totalCost ??
      recipe?.totalCost ??
      0
    );
    const fallbackIngredientTotal = deriveFallbackIngredientCost(recipe);
    const baseTotal = Number.isFinite(snapshotTotal) && snapshotTotal > 0
      ? snapshotTotal
      : (Number.isFinite(fallbackTotal) && fallbackTotal > 0
        ? fallbackTotal
        : (snapshotBreakdownTotal > 0 ? snapshotBreakdownTotal : fallbackIngredientTotal));

    return {
      isAlreadyScaled: isSnapshotAlreadyScaled,
      baseSrv: portionBasis,
      appliedPortion,
      effectiveMultiplier,
      displayedCostBreakdown: snapshotBreakdown,
      displayedCostTotal: baseTotal * effectiveMultiplier
    };
  }

  const scaling = resolveRecipeScalingContext(recipe, currentPortion);
  const targetPortion = scaling.targetPortion;
  const isAlreadyScaled = scaling.isPreScaled;
  const baseSrv = scaling.baseServings;
  const appliedPortion = scaling.appliedPortion;
  const effectiveMultiplier = scaling.effectiveMultiplier;

  const displayedCostBreakdown = (recipe?.costBreakdown || []).map((item) => {
    const line = Number(item?.total || item?.cost || 0);
    return isAlreadyScaled ? line : line * effectiveMultiplier;
  });

  const displayedCostTotal = displayedCostBreakdown.reduce(
    (sum, line) => sum + (Number(line) || 0),
    0
  );

  const fallbackTotal = Number(recipe?.totalCost ?? recipe?.presentation?.totalCost ?? 0);
  const adjustedFallbackTotal = isAlreadyScaled
    ? fallbackTotal
    : fallbackTotal * effectiveMultiplier;
  const fallbackIngredientTotal = deriveFallbackIngredientCost(recipe);

  return {
    isAlreadyScaled,
    baseSrv,
    appliedPortion,
    effectiveMultiplier,
    displayedCostBreakdown,
    displayedCostTotal:
      displayedCostBreakdown.length > 0
        ? displayedCostTotal
        : (adjustedFallbackTotal > 0 ? adjustedFallbackTotal : fallbackIngredientTotal)
  };
}
